import { BROWSER_HISTORY_STORAGE_KEY, useBrowserHistoryStore } from "./browserHistoryStore";
import {
  broadcastBrowserDpopKeyPurge,
  clearBrowserDpopKeyCache,
  DPOP_DATABASE_NAME,
} from "./cloud/dpop";
import { LAST_INVOKED_SCRIPT_BY_PROJECT_KEY } from "./components/ChatView.logic";
import { INBOX_READ_STORAGE_KEY_PREFIX } from "./components/inbox/inboxReadState";
import { COMPOSER_DRAFT_STORAGE_KEY, useComposerDraftStore } from "./composerDraftStore";
import { CONNECTION_RUNTIME_DATABASE_NAME } from "./connection/storage";
import { DIFF_PANEL_STORAGE_KEY, useDiffPanelStore } from "./diffPanelStore";
import { LEGACY_PROMPT_STASH_STORAGE_KEY, PROMPT_STASH_STORAGE_KEY } from "./promptStashStore";
import { RIGHT_PANEL_STORAGE_KEY, useRightPanelStore } from "./rightPanelStore";
import { LAST_PICKED_ORG_KEY } from "./state/orgs";
import { TERMINAL_UI_STATE_STORAGE_KEY, useTerminalUiStateStore } from "./terminalUiStateStore";
import { clearPersistedUiState, PERSISTED_STATE_KEY } from "./uiStateStore";

/**
 * localStorage keys that carry workspace *content* or tenant-linked metadata —
 * unsent drafts (with base64 attachments), the prompt stash, visited-thread
 * history, the per-thread panel and terminal state, the UI state store (thread
 * visit timestamps, changed-file expansion, per-project server workspace
 * roots), the last-picked organization, and the last script invoked per
 * project. Cosmetic preferences (theme, typography, layout widths,
 * confirm-dialog toggles, client settings, sidebar shelves) are deliberately
 * absent: they describe the device, not the account, and survive sign-out.
 */
export const WORKSPACE_STORAGE_KEYS: readonly string[] = [
  COMPOSER_DRAFT_STORAGE_KEY,
  PROMPT_STASH_STORAGE_KEY,
  LEGACY_PROMPT_STASH_STORAGE_KEY,
  BROWSER_HISTORY_STORAGE_KEY,
  RIGHT_PANEL_STORAGE_KEY,
  DIFF_PANEL_STORAGE_KEY,
  PERSISTED_STATE_KEY,
  TERMINAL_UI_STATE_STORAGE_KEY,
  LAST_PICKED_ORG_KEY,
  LAST_INVOKED_SCRIPT_BY_PROJECT_KEY,
];

/**
 * Key families whose members are dynamic, so the purge matches by prefix: the
 * inbox read receipts are scoped per server and signed-in subject.
 */
export const WORKSPACE_STORAGE_KEY_PREFIXES: readonly string[] = [INBOX_READ_STORAGE_KEY_PREFIX];

/**
 * IndexedDB databases holding thread/shell snapshots and the connection
 * catalog, plus the cloud-auth database holding this profile's DPoP proof
 * key — that key authenticates the browser to the relay, so it must not
 * outlive the session. It is lazily regenerated on the next relay use.
 */
export const WORKSPACE_INDEXED_DB_NAMES: readonly string[] = [
  CONNECTION_RUNTIME_DATABASE_NAME,
  DPOP_DATABASE_NAME,
];

/**
 * How long to hold a sign-out for a database deletion. A deletion blocked by
 * another tab stays queued in the browser and completes once that tab lets go,
 * so waiting longer buys nothing.
 */
const DEFAULT_INDEXED_DB_TIMEOUT_MS = 1_500;

export interface PurgeWorkspaceStorageOptions {
  readonly indexedDbTimeoutMs?: number;
}

/**
 * The zustand stores own their persisted keys: clearing through `persist`
 * also cancels the composer's pending debounced write, which a bare
 * `localStorage.removeItem` would leave armed for the `beforeunload` flush to
 * re-persist the draft after the purge.
 */
function clearPersistedStores(): void {
  for (const store of [
    useComposerDraftStore,
    useBrowserHistoryStore,
    useRightPanelStore,
    useDiffPanelStore,
    useTerminalUiStateStore,
  ]) {
    try {
      store.persist.clearStorage();
    } catch {
      // Storage unavailable — nothing persisted to clear.
    }
  }
  try {
    // Not a `persist` store: it owns its own debounced writer.
    clearPersistedUiState();
  } catch {
    // Storage unavailable — nothing persisted to clear.
  }
}

function removeWorkspaceKeys(): void {
  let storage: Pick<Storage, "removeItem" | "key" | "length">;
  try {
    if (typeof localStorage === "undefined") {
      return;
    }
    storage = localStorage;
  } catch {
    // Reading the `localStorage` property throws a SecurityError when the
    // origin is storage-blocked; then there is nothing to purge.
    return;
  }
  for (const key of WORKSPACE_STORAGE_KEYS) {
    try {
      storage.removeItem(key);
    } catch {
      // Best effort per key.
    }
  }
  // Snapshot the dynamic keys before deleting: removing during the walk
  // reindexes `storage.key(index)` and would skip entries.
  const prefixedKeys: string[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key !== null && WORKSPACE_STORAGE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        prefixedKeys.push(key);
      }
    }
  } catch {
    // Enumeration unavailable — the named keys above are already gone.
  }
  for (const key of prefixedKeys) {
    try {
      storage.removeItem(key);
    } catch {
      // Best effort per key.
    }
  }
}

function deleteDatabase(name: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      resolve();
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.deleteDatabase(name);
    } catch {
      resolve();
      return;
    }
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(settle, timeoutMs);
    request.addEventListener("success", settle);
    request.addEventListener("error", settle);
    // Blocked by a connection that will not close (another tab): the
    // deletion stays queued in the browser; do not hold the redirect for it.
    request.addEventListener("blocked", settle);
  });
}

/**
 * Purges the workspace content this browser profile holds for the signed-in
 * account: draft/stash/history/panel keys from localStorage and the
 * connection-runtime and cloud-auth IndexedDB databases (the latter holds the
 * browser's DPoP proof key, also dropped from memory). Never throws and never
 * waits longer than the database timeout, so the sign-out redirect always
 * follows.
 *
 * Call it immediately before a full navigation: the in-memory stores stay
 * live and would persist again on their next state change.
 */
export async function purgeWorkspaceStorage(
  options: PurgeWorkspaceStorageOptions = {},
): Promise<void> {
  const timeoutMs = options.indexedDbTimeoutMs ?? DEFAULT_INDEXED_DB_TIMEOUT_MS;
  clearPersistedStores();
  removeWorkspaceKeys();
  // Deleting the database is not enough while this page stays open: the
  // signer caches the key in memory and would keep proving with it.
  clearBrowserDpopKeyCache();
  // Other open tabs share the database but not this module cache.
  broadcastBrowserDpopKeyPurge();
  await Promise.all(WORKSPACE_INDEXED_DB_NAMES.map((name) => deleteDatabase(name, timeoutMs)));
  // A store that changed while the deletion was in flight has re-persisted;
  // sweep the keys once more so the redirect leaves nothing behind.
  clearPersistedStores();
  removeWorkspaceKeys();
}
